import { useCandidateTracks, useMCAInfo } from 'contexts/AuthContext'
import { useCallback, useState } from 'react'
import _ from 'lodash'
import { ScreenKey } from '../util'
import {
  alignRetrySideEffectsPromise,
  alignTracksPromise,
  getMcaEligibilityStatusPromise,
  getMcaEnrollmentPromise,
  identityCompletePromise,
  resubmitMbiPromise,
  screenerAnswersPromise,
  submitEligibilityPromise,
} from 'apis/FitOnCareAPI'
import { logAmplitudeEvent } from 'utils/amplitudeInit'
import { getMbiFailedInfo, setMbiFailedInfo } from './mbiFailedCache'
import {
  isMcaStateRestricted,
  MCA_STATE_NOT_SUPPORTED,
  setRestrictedState,
  useMcaStateGate,
} from './mcaStateGate'
import { logBrazeEvent, setBrazeProfile } from 'utils/brazeInit'
import { useFeatureFlagByName } from 'contexts/FeatureFlagContext'

const NONE_OF_THE_ABOVE = 'none_of_above'
const MAX_POLL_DURATION_MS = 30000

function isSingleNoneOfTheAbove(selections = []) {
  return selections.length === 1 && selections[0]?.value === NONE_OF_THE_ABOVE
}

export const useMCA = ({ sku }) => {
  const { mcaInfo, setMCAInfo } = useMCAInfo()
  const [showCheckingSpinner, setShowCheckingSpinner] = useState(false)

  const {
    wlcCondition,
    wlcExclusion,
    mskPainQualifier,
    mskExclusion,
    mskBodyAreas,
  } = mcaInfo
  const { candidateTracks, setCandidateTracks } = useCandidateTracks()
  const { flags, loaded: flagsLoaded } = useFeatureFlagByName({
    keys: ['show_msk'],
  })
  const showMsk = flags.find((flag) => flag.key === 'show_msk')?.enabled
  const { lookupCurrentUserState } = useMcaStateGate()

  const markNotEligible = () => {
    return ScreenKey.NOT_ELIGIBLE
  }

  const gotoStateComingSoon = async () => {
    const state = await lookupCurrentUserState()
    setRestrictedState(state)
    return ScreenKey.MCA_COMING_SOON_STATE
  }

  const getMcaEnrollment = useCallback(async () => {
    const enrollmentResp = await getMcaEnrollmentPromise()
    const enrollmentData = enrollmentResp?.data || {}
    const existingCareSkus = enrollmentData?.existingCareSkus || []
    setBrazeProfile({
      mcaEnrolledAt: enrollmentData?.enrollment?.createdAt || '',
      sku: existingCareSkus,
    })

    const candidateTracks = enrollmentData?.enrollment?.candidateTracks
    if (candidateTracks) {
      setCandidateTracks(candidateTracks)
    }
    return enrollmentData
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getCurrentEnrollment = useCallback(async () => {
    const enrollmentResp = await getMcaEnrollment()
    const alreadyEnrolledInCareSku = enrollmentResp?.alreadyEnrolledInCareSku

    if (sku === 'mca') {
      const state = await lookupCurrentUserState()
      if (isMcaStateRestricted(state)) {
        setRestrictedState(state)
        return { currentKey: ScreenKey.MCA_COMING_SOON_STATE, enrollmentResp }
      }
    }

    if (alreadyEnrolledInCareSku) {
      return { currentKey: ScreenKey.MCA_ALREADY_ENROLLED, enrollmentResp }
    }
    const enrollment = enrollmentResp?.enrollment
    const status = enrollment?.status
    const mbiRetryCount = enrollment?.mbiRetryCount || 0
    switch (status) {
      case 'cms_eligibility_error':
        setMbiFailedInfo({
          mbiNotFoundReason: status,
          mbiNotFoundAttemptNumber:
            mbiRetryCount ||
            (+getMbiFailedInfo().mbiNotFoundAttemptNumber || 0) + 1,
        })
        return { currentKey: ScreenKey.MCA_MBI_NOT_FOUND, enrollmentResp }
      case 'cms_eligible':
        return {
          currentKey: enrollment?.clearVerifiedAt
            ? ScreenKey.MCA_ELIGIBLE
            : ScreenKey.MCA_VERIFY_WITH_CLEAR,
          enrollmentResp,
        }
      case 'identity_failed':
        return { currentKey: ScreenKey.MCA_VERIFY_WITH_CLEAR, enrollmentResp }
      case 'control_group':
        return {
          currentKey: ScreenKey.MCA_THANKS_FOR_YOUR_INTEREST,
          enrollmentResp,
        }
      default:
        return { currentKey: ScreenKey.MCA_ABOUT_YOU, enrollmentResp }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getNextStage = useCallback(
    async ({ nextStage, status, enrollment }) => {
      let nextScreenKey
      switch (nextStage) {
        case 'verification_failure':
          nextScreenKey = ScreenKey.MCA_VERIFY_WITH_CLEAR_FAILURE
          break
        case 'control_group':
          setMbiFailedInfo({
            unavailableReason: 'Control Group',
            unavailableDetail: nextStage,
          })
          nextScreenKey = ScreenKey.MCA_THANKS_FOR_YOUR_INTEREST
          break
        case 'mbi_not_found':
          setMbiFailedInfo({
            mbiNotFoundReason: nextStage,
            mbiNotFoundAttemptNumber:
              enrollment?.mbiRetryCount ||
              (+getMbiFailedInfo().mbiNotFoundAttemptNumber || 0) + 1,
          })
          nextScreenKey = ScreenKey.MCA_MBI_NOT_FOUND
          break
        case 'clear':
          nextScreenKey = ScreenKey.MCA_VERIFY_WITH_CLEAR
          break
        case 'already_aligned':
          nextScreenKey = ScreenKey.MCA_ALREADY_ENROLLED
          break
        case 'cms_exclusion':
          setMbiFailedInfo({
            unavailableReason: 'CMS Exclusion',
            unavailableDetail: nextStage,
          })
          logBrazeEvent(`User Denied: CMS Ineligible - ${sku}`)
          nextScreenKey = ScreenKey.NOT_ELIGIBLE
          break
        case 'not_medicare':
          // CMS terminal denial: member isn't enrolled in Medicare fee-for-service.
          // Not an MBI re-prompt — re-entering the MBI can't change it — so show the
          // Not Eligible page like other CMS denials. See CARE-104 (BE) / CARE-106.
          setMbiFailedInfo({
            unavailableReason: 'Not Medicare FFS',
            unavailableDetail: nextStage,
          })
          logBrazeEvent(`User Denied: CMS Ineligible - ${sku}`)
          nextScreenKey = ScreenKey.NOT_ELIGIBLE
          break
        case 'error_retry':
          if (status === 'alignment_failed') {
            nextScreenKey = ScreenKey.MCA_ALIGNMENT_FAILED
          } else {
            setMbiFailedInfo({
              mbiNotFoundReason: nextStage,
              mbiNotFoundAttemptNumber:
                enrollment?.mbiRetryCount ||
                (+getMbiFailedInfo().mbiNotFoundAttemptNumber || 0) + 1,
            })
            nextScreenKey = ScreenKey.MCA_MBI_NOT_FOUND
          }
          break
        case 'error_retry_side_effects':
          // CMS aligned but downstream side-effects failed — the row is alignment_failed
          // WITH a cms_alignment_id, so the AlignmentFailed screen's retry-side-effects is
          // valid. (A transient $align failure keeps the row cms_eligible and is routed to
          // the same screen by alignTracks — see below.)
          nextScreenKey = ScreenKey.MCA_ALIGNMENT_FAILED
          break
        case 'pcp':
          nextScreenKey = ScreenKey.MCA_PRIMARY_CARE_PROVIDER
          break
        case 'eligible_congrats':
          nextScreenKey = ScreenKey.MCA_ELIGIBLE
          break
        case 'submit_error':
          setMbiFailedInfo({
            unavailableReason: 'CMS Exclusion',
            unavailableDetail: nextStage,
          })
          logBrazeEvent(`User Denied: CMS Ineligible - ${sku}`)
          nextScreenKey = ScreenKey.NOT_ELIGIBLE
          break
        default:
          setMbiFailedInfo({
            unavailableReason: 'Condition',
            unavailableDetail: nextStage,
          })
          nextScreenKey = ScreenKey.NOT_ELIGIBLE
      }
      return nextScreenKey
    },
    [sku]
  )

  const getPendingScreenKey = useCallback(
    async (startedAt = Date.now()) => {
      if (Date.now() - startedAt >= MAX_POLL_DURATION_MS) {
        return await getNextStage({ nextStage: 'submit_error' })
      }

      const eligibilityStatusResp = await getMcaEligibilityStatusPromise()
      const eligibilityStatusNextStage = eligibilityStatusResp?.data?.nextStage
      const status = eligibilityStatusResp?.data?.status
      const pollAfterMs = eligibilityStatusResp?.data?.pollAfterMs || 3000

      if (status !== 'pending') {
        return await getNextStage({ nextStage: eligibilityStatusNextStage })
      }

      if (Date.now() - startedAt + pollAfterMs > MAX_POLL_DURATION_MS) {
        return await getNextStage({ nextStage: 'submit_error' })
      }

      await new Promise((resolve) => {
        setTimeout(resolve, pollAfterMs)
      })
      return await getPendingScreenKey(startedAt)
    },
    [getNextStage]
  )

  const submitEligibility = useCallback(async () => {
    const resp = await submitEligibilityPromise()
    if (resp.code === 200) {
      const nextStage = resp.data.nextStage
      const status = resp.data.status

      logAmplitudeEvent('Screen View: Onboarding - CMS Eligibility', {
        sku,
        status,
      })
      logBrazeEvent(`User Eligible - ${sku}`)
      if (status === 'pending') {
        return await getPendingScreenKey()
      }
      return getNextStage({ nextStage })
    }
    setMbiFailedInfo({
      unavailableReason: 'CMS Exclusion',
      unavailableDetail: resp?.code,
    })
    return ScreenKey.NOT_ELIGIBLE
  }, [getNextStage, getPendingScreenKey, sku])

  const submitCMS = useCallback(
    async (overrides = {}) => {
      setShowCheckingSpinner(true)
      // mskBodyAreas is chosen on PAIN_LOCATED and submitted in the same click, before
      // the setMCAInfo update has propagated into this closure. Accept a fresh override
      // so we don't submit a stale (empty) bodyAreas and trip backend msk_body_areas_required.
      const bodyAreas = overrides.mskBodyAreas ?? mskBodyAreas
      const resp = await screenerAnswersPromise({
        wlc: {
          qualifyingConditions: wlcCondition?.map((item) => item.value),
          exclusions: wlcExclusion?.map((item) => item.value),
        },
        msk: {
          painQualifier: mskPainQualifier?.[0]?.value,
          dailyImpact: mskPainQualifier?.[0]?.value,
          exclusions: mskExclusion?.map((item) => item.value),
          bodyAreas: bodyAreas?.map((item) => item.value),
        },
      })
      let nextScreenKey = markNotEligible()
      if (resp.code === 200) {
        const nextStage = resp.data.nextStage
        if (nextStage === 'cms_eligibility') {
          nextScreenKey = await submitEligibility()
        } else {
          nextScreenKey = getNextStage({ nextStage })
        }
      }
      setShowCheckingSpinner(false)
      return nextScreenKey
    },
    [
      getNextStage,
      mskBodyAreas,
      mskExclusion,
      mskPainQualifier,
      submitEligibility,
      wlcCondition,
      wlcExclusion,
    ]
  )

  const getNextScreenKey = useCallback(
    async (currentKey, overrides = {}) => {
      const patchMCAInfo = (patch) => setMCAInfo({ ...mcaInfo, ...patch })

      switch (currentKey) {
        case ScreenKey.MCA_WLC_CONDITION: {
          if (isSingleNoneOfTheAbove(wlcCondition)) {
            patchMCAInfo({ wlc_candidate: false })
            return ScreenKey.MCA_MSK_PAIN_QUALIFIER
          }
          if (!_.isEmpty(wlcExclusion)) {
            const wlcCandidate =
              !isSingleNoneOfTheAbove(wlcCondition) &&
              isSingleNoneOfTheAbove(wlcExclusion)
            patchMCAInfo({ wlc_candidate: wlcCandidate })
            if (showMsk) {
              return ScreenKey.MCA_MSK_PAIN_QUALIFIER
            } else {
              return await submitCMS()
            }
          }
          return ScreenKey.MCA_WLC_EXCLUSION
        }

        case ScreenKey.MCA_WLC_EXCLUSION: {
          const wlcCandidate =
            !isSingleNoneOfTheAbove(wlcCondition) &&
            isSingleNoneOfTheAbove(wlcExclusion)
          patchMCAInfo({ wlc_candidate: wlcCandidate })
          if (showMsk) {
            return ScreenKey.MCA_MSK_PAIN_QUALIFIER
          } else {
            return await submitCMS()
          }
        }

        case ScreenKey.MCA_MSK_PAIN_QUALIFIER: {
          const isMskPainQualifierYes = mskPainQualifier[0]?.name === 'Yes'
          const isMskCandidate =
            isSingleNoneOfTheAbove(mskExclusion) && isMskPainQualifierYes
          const isWlcCandidate = mcaInfo.wlc_candidate

          if (isMskPainQualifierYes) {
            if (_.isEmpty(mskExclusion)) {
              return ScreenKey.MCA_MSK_EXCLUSION
            }
            patchMCAInfo({ msk_candidate: isMskCandidate })
            if (!isMskCandidate && !isWlcCandidate) {
              setMbiFailedInfo({
                unavailableReason: 'Condition',
                unavailableDetail: 'msk and wlc candidate false',
              })
              logBrazeEvent(`User Denied: Condition - ${sku}`)
              return markNotEligible()
            }
            return await submitCMS()
          }

          patchMCAInfo({ msk_candidate: false })
          if (!isWlcCandidate) {
            setMbiFailedInfo({
              unavailableReason: 'Condition',
              unavailableDetail: 'wlc candidate false',
            })
            logBrazeEvent(`User Denied: Condition - ${sku}`)
            return markNotEligible()
          }
          return await submitCMS()
        }

        case ScreenKey.MCA_MSK_EXCLUSION: {
          const isMskExclusionNone = isSingleNoneOfTheAbove(mskExclusion)
          const isMskPainQualifierYes = mskPainQualifier[0]?.name === 'Yes'
          patchMCAInfo({
            msk_candidate: isMskExclusionNone && isMskPainQualifierYes,
          })
          if (isMskExclusionNone) {
            return ScreenKey.PAIN_LOCATED
          }
          return await submitCMS()
        }

        case ScreenKey.PAIN_LOCATED: {
          const {
            msk_candidate: isMskCandidate,
            wlc_candidate: isWlcCandidate,
          } = mcaInfo
          if (!isMskCandidate && !isWlcCandidate) {
            setMbiFailedInfo({
              unavailableReason: 'Condition',
              unavailableDetail: 'msk and wlc candidate false',
            })
            logBrazeEvent(`User Denied: Condition - ${sku}`)
            return markNotEligible()
          }
          return await submitCMS(overrides)
        }

        default:
          break
      }
    },
    [
      setMCAInfo,
      mcaInfo,
      wlcCondition,
      wlcExclusion,
      showMsk,
      submitCMS,
      mskPainQualifier,
      mskExclusion,
      sku,
    ]
  )

  const resubmitMbi = useCallback(
    async ({
      firstName,
      lastName,
      birthday,
      gender,
      medicareBeneficiaryId,
    }) => {
      const resp = await resubmitMbiPromise({
        firstName,
        lastName,
        dateOfBirth: birthday,
        gender,
        mbi: medicareBeneficiaryId,
      })
      if (resp.code === 200) {
        const status = resp.data.status
        const nextStage = resp.data.nextStage
        const enrollment = resp.data?.enrollment
        if (status !== 'cms_eligibility_error') {
          if (nextStage === 'cms_eligibility') {
            return await submitEligibility()
          } else {
            return getNextStage({ nextStage, enrollment })
          }
        } else {
          const mbiNotFoundAttemptNumber =
            enrollment?.mbiRetryCount ||
            (+getMbiFailedInfo().mbiNotFoundAttemptNumber || 0) + 1

          setMbiFailedInfo({
            mbiNotFoundReason: resp.data.status,
            mbiNotFoundAttemptNumber,
          })
          return ScreenKey.MCA_MBI_NOT_FOUND
        }
      } else if (resp.code === 429) {
        setMbiFailedInfo({
          unavailableReason: 'CMS Exclusion',
          unavailableDetail: resp?.status,
        })
        return ScreenKey.NOT_ELIGIBLE
      } else if (resp?.msg === MCA_STATE_NOT_SUPPORTED) {
        return await gotoStateComingSoon()
      } else {
        return ScreenKey.MCA_MBI_NOT_FOUND
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getNextStage, submitEligibility]
  )

  const identityComplete = useCallback(
    async ({ state, outcome, fitonHealthUserId }) => {
      const resp = await identityCompletePromise({
        state,
        outcome,
        fitonHealthUserId,
      })
      if (resp.code === 200) {
        const nextStage = resp.data.nextStage
        if (nextStage === 'eligible_congrats') {
          logBrazeEvent(`User Verified - mca`)
        }
        return getNextStage({ nextStage })
      } else {
        setMbiFailedInfo({
          unavailableReason: 'Identity',
          unavailableDetail: resp?.code,
        })
        return markNotEligible()
      }
    },
    [getNextStage]
  )

  const alignRetrySideEffects = useCallback(async () => {
    const resp = await alignRetrySideEffectsPromise()
    if (resp.code === 200) {
      const nextStage = resp.data.nextStage
      return getNextStage({ nextStage })
    }
    setMbiFailedInfo({
      unavailableReason: 'Alignment',
      unavailableDetail: resp?.code,
    })
    return markNotEligible()
  }, [getNextStage])

  const alignTracks = useCallback(
    async ({
      acceptedTracks,
      participationVoluntary,
      oneParticipantPerTrack,
      mayDisenrollAfter90Days,
      costSharingAcknowledged,
    }) => {
      const resp = await alignTracksPromise({
        acceptedTracks,
        participationVoluntary,
        oneParticipantPerTrack,
        mayDisenrollAfter90Days,
        costSharingAcknowledged,
      })
      if (resp.code === 200) {
        const nextStage = resp.data.nextStage
        const enrollment = resp.data.enrollment
        const candidateTracks = enrollment?.candidateTracks
        if (candidateTracks) {
          setCandidateTracks(candidateTracks)
        }
        // Both alignment-failure modes recover on the AlignmentFailed screen; its retry
        // picks the right endpoint from the row status (re-align vs retry-side-effects).
        // Route them here rather than through getNextStage's shared 'error_retry' branch,
        // which is eligibility-oriented (a transient $align failure keeps status=cms_eligible
        // and would otherwise be sent to the MBI-not-found screen).
        if (
          nextStage === 'error_retry' ||
          nextStage === 'error_retry_side_effects'
        ) {
          return ScreenKey.MCA_ALIGNMENT_FAILED
        }
        // Healthie tags are applied by the API for the tracks CMS aligned. A 200 can also be a
        // CMS rejection (cms_exclusion, control_group, already_aligned), which approves nothing.
        const alignedSkus =
          resp.data.status === 'aligned'
            ? (resp.data.alignedTracks || resp.data.acceptedTracks || []).map(
                (track) => (track === 'eckm' ? 'wlc' : 'msk')
              )
            : []
        if (alignedSkus.length) {
          logBrazeEvent(`User Approved - ${sku}`)
          alignedSkus.forEach((alignedSku) =>
            logBrazeEvent(`User Approved - ${alignedSku}`)
          )
          setBrazeProfile({ sku: alignedSkus })
        }
        return getNextStage({ nextStage })
      }
      if (resp?.msg === MCA_STATE_NOT_SUPPORTED) {
        // CARE-151's last line of defense: /align refused the state. Not an
        // alignment failure to retry — the answer won't change.
        return await gotoStateComingSoon()
      }
      return ScreenKey.MCA_ALIGNMENT_FAILED
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getNextStage, sku]
  )

  const openJoinScreen = useCallback(async () => {
    let _candidateTracks = candidateTracks
    if (_.isEmpty(_candidateTracks)) {
      const enrollmentResp = await getMcaEnrollment()
      _candidateTracks = enrollmentResp?.enrollment?.candidateTracks
    }
    if (_candidateTracks.includes('eckm')) {
      return ScreenKey.SKU_CONGRATULATIONS
    } else if (_candidateTracks.includes('msk')) {
      return ScreenKey.REHAB_CONGRATULATIONS
    }
    setMbiFailedInfo({
      unavailableReason: 'Condition',
      unavailableDetail: 'candidate tracks empty',
    })
    return ScreenKey.NOT_ELIGIBLE
  }, [candidateTracks, getMcaEnrollment])

  return {
    mcaInfo,
    setMCAInfo,
    getNextScreenKey,
    showCheckingSpinner,
    flagsLoaded,
    resubmitMbi,
    getCurrentEnrollment,
    identityComplete,
    alignRetrySideEffects,
    alignTracks,
    openJoinScreen,
    getMcaEnrollment,
  }
}
